import { z } from "zod"

export const nativeBridgeVersion = 1
export type NativeMethod = "getCapabilities" | "getAppState" | "setBackSwipe" | "getIdentity" | "signIn" | "signOut" | "requestPhone" | "completePhone" | "getPushDevice" | "navigate" | "back" | "refresh" | "close" | "openExternal"
const sessionSchema = z.object({ nonce: z.string().min(1), platform: z.string(), version: z.literal(nativeBridgeVersion), appVersion: z.string().min(1), capabilities: z.array(z.string()), events: z.array(z.string()).optional() })
export type NativeBridgeSession = z.infer<typeof sessionSchema>
declare global {
  interface Window {
    CashHubPlatform?: { postMessage(message: string): void }
    __cashhubNativeSession?: NativeBridgeSession
    __cashhubNavigate?: (path: string, mode?: "auto" | "replace") => boolean
    __cashhubHandleNativeBack?: () => boolean
  }
}
export const isNativeApp = (): boolean => typeof window !== "undefined" && /CashHubHybrid\//.test(window.navigator.userAgent)
export const nativePlatform = (): string | null => (typeof window === "undefined" ? null : (window.__cashhubNativeSession?.platform ?? null))
export class NativeCapabilityError extends Error {
  override readonly name = "NativeCapabilityError"
  constructor(readonly code: string) {
    super(code)
  }
}
export const supportsNativeCapability = (method: NativeMethod, session: unknown = typeof window === "undefined" ? undefined : window.__cashhubNativeSession): boolean => {
  const parsed = sessionSchema.safeParse(session)
  return parsed.success && parsed.data.capabilities.includes(method)
}
const responseSchema = z.discriminatedUnion("ok", [
  z.object({ id: z.string(), nonce: z.string().min(1), version: z.literal(nativeBridgeVersion), ok: z.literal(true), value: z.record(z.string(), z.unknown()) }),
  z.object({ id: z.string(), nonce: z.string().min(1), version: z.literal(nativeBridgeVersion), ok: z.literal(false), error: z.string() }),
])
export const waitForNativeBridge = async (): Promise<NativeBridgeSession> => {
  if (!isNativeApp() || window !== window.top) throw new NativeCapabilityError("UNAVAILABLE")
  if (window.__cashhubNativeSession === undefined || window.CashHubPlatform === undefined) {
    await new Promise<void>((resolve, reject) => {
      const ready = (): void => {
        if (window.__cashhubNativeSession === undefined || window.CashHubPlatform === undefined) return
        clearTimeout(timeout)
        window.removeEventListener("cashhub:native-ready", ready)
        resolve()
      }
      const timeout = setTimeout(() => {
        window.removeEventListener("cashhub:native-ready", ready)
        reject(new NativeCapabilityError("UNAVAILABLE"))
      }, 10_000)
      window.addEventListener("cashhub:native-ready", ready)
    })
  }
  const session = sessionSchema.safeParse(window.__cashhubNativeSession)
  if (!session.success) throw new NativeCapabilityError("UNAVAILABLE")
  return session.data
}
export const requestNativeCapability = async (method: NativeMethod, params: Readonly<Record<string, string>> = {}): Promise<Record<string, unknown>> => {
  const session = await waitForNativeBridge()
  if (!supportsNativeCapability(method, session)) throw new NativeCapabilityError("UNSUPPORTED")
  const channel = window.CashHubPlatform
  if (channel === undefined) throw new NativeCapabilityError("UNAVAILABLE")
  const id = crypto.randomUUID()
  const message = JSON.stringify({ id, method, params, nonce: session.nonce, version: nativeBridgeVersion })
  if (message.length > 8192) throw new NativeCapabilityError("INVALID_INPUT")
  return new Promise((resolve, reject) => {
    const cleanup = (): void => {
      clearTimeout(timeout)
      window.removeEventListener("cashhub:native-result", receive)
      window.removeEventListener("cashhub:native-ready", documentChanged)
      window.removeEventListener("pagehide", navigation)
    }
    const navigation = (): void => {
      cleanup()
      reject(new NativeCapabilityError("DOCUMENT_CHANGED"))
    }
    const documentChanged = (): void => {
      if (window.__cashhubNativeSession?.nonce !== session.nonce) navigation()
    }
    const receive = (event: Event): void => {
      const parsed = responseSchema.safeParse((event as CustomEvent<unknown>).detail)
      if (!parsed.success || parsed.data.id !== id || window.__cashhubNativeSession?.nonce !== session.nonce) return
      if (parsed.data.nonce !== session.nonce) return
      cleanup()
      if (parsed.data.ok) resolve(parsed.data.value)
      else reject(new NativeCapabilityError(parsed.data.error))
    }
    const timeout = setTimeout(() => {
      cleanup()
      reject(new NativeCapabilityError("TIMEOUT"))
    }, 180_000)
    window.addEventListener("cashhub:native-result", receive)
    window.addEventListener("cashhub:native-ready", documentChanged)
    window.addEventListener("pagehide", navigation)
    try {
      channel.postMessage(message)
    } catch {
      cleanup()
      reject(new NativeCapabilityError("UNAVAILABLE"))
    }
  })
}
